import * as i1 from '@angular/common/http';
import { HttpClient, HttpHeaders, HttpResponse, HTTP_INTERCEPTORS, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, NgModule } from '@angular/core';
import { __decorate, __metadata } from 'tslib';
import { ExceptionHandlerClass, ErrorCodes, RequestConfig, Events, PendingResponse, Send, SendV5, ActionModel, ActionModelV5, Inject as Inject$1, Constants, NetworkException, ContentType, Modal, Order, Request, RequestType, View, App, AppV5, ModelV5, Resolve, RequestBuilderV5, EventBehavior, ExceptionHandler, ModelFactoryV5 } from '@mobilize/webmap-core';
import { of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { WMLogger } from '@mobilize/logging';

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * InjectionToken used to provide a default WebMapServiceConfig to the Dependency Injection.
 */
const WEBMAP_CONFIG = new InjectionToken('WebMap Service Configuration');
/**
 * Default configuration for the WebMap Service.
 */
const WEBMAP_DEFAULT_CONFIG = {
    dataManagerBusyLoaderTime: 0,
    useBundleEvent: false,
    useDynamicServerEvents: true,
    usePercentage: false,
    webMapVersion: 'v5',
    wmSourceVersion: 'WF',
    showBrowserContextMenu: false,
    baseHref: '',
    styleProperties: [
        'BackColor',
        'ForeColor',
        'BackgroundImage',
        'Top',
        'Right',
        'Left',
        'Bottom',
        'Height',
        'Width',
        'Location',
        'Font',
        'FontFamily',
        'TextDecoration',
    ],
};

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class WMConstants {
}
WMConstants.version4 = 'v4';
WMConstants.version5 = 'v5';
WMConstants.sourceVersionPB = 'PB';
WMConstants.sourceVersionWin = 'WF';
WMConstants.sourceVersionWebForms = 'WEBF';
/**
 * Session storage tab id entry of web map service to identify
 * specific session when multi tab feature is enable
 */
WMConstants.sessionStorageTabIdEntry = 'TabId';
/**
 * Header of MultiTab feature to send session id to back-end
 */
WMConstants.multiTabHeader = 'WM-Tb';

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
var WebMapUtils_1;
let WebMapUtils = WebMapUtils_1 = class WebMapUtils {
    static getPropertyName(name) {
        name = name.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        name = name.replace(/^\./, ''); // strip a leading dot
        const nameArray = name.split('.');
        return nameArray[nameArray.length - 1];
    }
    static getProperty(obj, name) {
        name = name.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        name = name.replace(/^\./, ''); // strip a leading dot
        const nameArray = name.split('.');
        const result = new Map();
        result.set(0, obj);
        for (let i = 0, n = nameArray.length; i < n; ++i) {
            const model = result.get(i);
            const currentProp = nameArray[i];
            if (model && currentProp in model) {
                result.set(i + 1, model[currentProp]);
            }
            else {
                return;
            }
        }
        return result.get(result.size - 1);
    }
    static getPropertyParent(obj, name) {
        name = name.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        name = name.replace(/^\./, ''); // strip a leading dot
        const nameArray = name.split('.');
        const result = new Map();
        result.set(0, obj);
        for (let i = 0, n = nameArray.length - 1; i < n; ++i) {
            const model = result.get(i);
            const currentProp = nameArray[i];
            /* c8 ignore next 5 */
            if (model && currentProp in model) {
                result.set(i + 1, model[currentProp]);
            }
            else {
                return;
            }
        }
        return result.get(result.size - 1);
    }
    static getRoutingName(name) {
        return name.replace('.', '-').toLowerCase();
    }
    static getDataQuery(url, id, action) {
        return action !== '' ? `${url}/${action}/${id}` : `${url}/${id}`;
    }
    static getWebApiHeaders() {
        const basicHeaders = {
            Accept: 'application/json',
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json',
            Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
            Pragma: 'no-cache',
        };
        // add Multi Tab header if it exists
        const id = sessionStorage.getItem(WMConstants.sessionStorageTabIdEntry);
        if (id) {
            basicHeaders[WMConstants.multiTabHeader] = id;
        }
        basicHeaders[WebMapUtils_1.WebApiHeader] = 'true';
        return basicHeaders;
    }
    /**
     * Identifies if a request contains the WebMap WebAPI headers.
     * @param request the Http Request
     */
    static isWebMapWebAPI(request) {
        return request.headers.get(WebMapUtils_1.WebApiHeader) === 'true';
    }
    /**
     * Identifies if a request contains the WebMap WebAPI headers.
     * @param request the Http Request
     */
    static getPendingResponseKey(response) {
        return response.headers.get(WebMapUtils_1.PendingResponseHeader);
    }
};
/**
 * Http Header used for a Web API request that requires the WebMap Services
 * to be initialized in the server.
 */
WebMapUtils.WebApiHeader = 'WM-API';
/**
 * Http Header used for a Web API response that has a pending response that
 * must be retrieved from the server.
 */
WebMapUtils.PendingResponseHeader = 'WM-Pending';
/**
 * Http header used for the WebMap server to identify a request that is
 * intended to created a new Application.
 */
WebMapUtils.getNewAppHeader = 'WM-NewApp';
WebMapUtils = WebMapUtils_1 = __decorate([
    ExceptionHandlerClass(ErrorCodes.AngularClient)
], WebMapUtils);

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * InjectionToken used to provide a custom configuration for the WebMAP application
 */
const WEBMAP_APP_CONFIG = new InjectionToken('WebMap Application Configuration');

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * @classdesc Provides the access to the WebMap Application.
 */
// @dynamic
class WebMapService {
    /**
     * Provides the WebMapService with the specified configuration.
     * To be used when defining the root application providers.
     * @param config
     */
    /* c8 ignore start */
    static provide(config) {
        return {
            deps: [HttpClient],
            provide: WebMapService,
            useFactory: (http) => new WebMapService(http, config),
        };
    }
    /* c8 ignore stop */
    /**
     * Gets the default configuration for a WebMapService.
     * @returns The default configuration object.
     */
    /* c8 ignore next 3 */
    static getDefaultConfig() {
        return WEBMAP_DEFAULT_CONFIG;
    }
    /**
     * Creates a new instance of WebMapService with the specified HttpClient and
     * configuration.
     * @param http The HttpClient.
     * @param config Optional service configuration.
     */
    /* c8 ignore start */
    constructor(http, config, appConfig) {
        this.http = http;
        /**
         * Flag that indicates if events
         * must be sent to the server side
         */
        this.sendEventsToServer = true;
        /**
         * Flag that indicates that the events must be recorded
         * and sent as a batch.
         */
        this.recordingRequests = false;
        /**
         * Keeps the record of events that are sent to server side as a batch
         */
        this.recordedRequests = [];
        this.config = config ? config : WebMapService.getDefaultConfig();
        this.core = new AngularApp(this.config, appConfig).currentApp;
        window['app'] = this.core;
        this.forms = {};
    }
    /**
     * Initializes the WebMapService with the specified router.
     * @param router The router instance of @angular/router.
     * @param appName The name of the application to send in the AppStart
     * @param callback The callback function to be executed
     * @param params Optional parameter to send queryString params when AppStart POST
     * request is call, the format of params should be 'param=1&param=2&param=3&param...'
     */
    /* c8 ignore start */
    init(appname, router, callback, params) {
        WebMapService.currentService = this;
        this.appname = appname ? appname : null;
        this.router = router ? router : null;
        const requestConfig = new RequestConfig();
        requestConfig.headers[WebMapUtils.getNewAppHeader] = 'true';
        const qryParam = this.tryObtainQueryParams(params);
        const action = this.processAppStartActionWithQueryParams(qryParam);
        this.core.init('', action, callback, this.appname, requestConfig, qryParam);
        this.core.subscribeEvent(Events.ApplicationRestart, () => this.restartApplication());
        this.core.subscribeEvent(Events.ReloadPendingViews, (viewId) => this.reloadPendingViews(viewId));
        this.core.subscribeEvent(Events.ApplyMultipleTab, (multipleTabHeader) => this.registerBrowserTabSession(multipleTabHeader));
    }
    /* c8 ignore stop */
    /**
     * Restart the WebMapService with the specified router.
     */
    /* c8 ignore next 3 */
    restartApplication() {
        this.createPreprareStartUpRequest();
    }
    /**
     * Remove pending views from ViewManager.
     */
    /* c8 ignore start */
    reloadPendingViews(viewId) {
        const pendingViews = this.core.getPendingViews();
        if (pendingViews.length > 0) {
            pendingViews.values().forEach((view) => {
                if (this.shouldCallPendingView(viewId, view)) {
                    view.load();
                }
            });
        }
    }
    /* c8 ignore stop */
    /**
     * Gets the model with the specified identifier.
     * @param uniqueId The model unique identifier.
     * @returns The IModel with the corresponding identifier, or null if not found.
     */
    /* c8 ignore next 3 */
    getModel(uniqueId) {
        return Promise.resolve(this.core.getModel(uniqueId));
    }
    /**
     * Adds an new model in models
     * @param newModel The model. of style
     */
    addModel(newModel) {
        this.core.addModel(newModel);
    }
    /**
     * Invokes the specified command inside the WebMapService.
     * @param command The command.
     */
    invoke(command) {
        this.core.invoke(command);
    }
    /**
     * Notifies that a property of a model has been changed. Used to keep track of
     * changes that must be sent to the server.
     * @param model The model whose property changed.
     * @param property The property that changed.
     */
    /* c8 ignore start */
    notifyChange(model, property) {
        const parent = WebMapUtils.getPropertyParent(model, property);
        // if the retrieved parent is a model
        if (parent.UniqueID || parent.id) {
            const id = parent.UniqueID ?? parent.id;
            // Validate if model still exists
            if (this.core.getModel(id)) {
                const name = WebMapUtils.getPropertyName(property);
                this.core.publishEvent(Events.Change, {
                    model: parent,
                    property: name,
                });
            }
            else {
                WMLogger.instance().error(`Trying to notify a property change for an invalid model with Id: ${id}`);
            }
        }
        else {
            // its a subproperty ex. Text.Value
            const id = model.UniqueID ?? model.id;
            // Validate if model still exists
            if (this.core.getModel(id)) {
                this.core.publishEvent(Events.Change, { model, property });
            }
            else {
                WMLogger.instance().error(`Trying to notify a property change for an invalid model with Id: ${id}`);
            }
        }
    }
    /* c8 ignore stop */
    /**
     * Fetches the component data as an array of the specified type.
     * @param url the url for the generic component.
     * @param id the identifier of the object.
     * @param [parameters] optional HttpParams object
     * @param [model] the current component model
     * @param [itemsExtractor] a function that extracts the desired collection from the component model used in V4 core versions.
     */
    fetch(url, id, parameters, model, itemsExtractor, action = '') {
        /* c8 ignore else */
        if (this.config.webMapVersion === WMConstants.version4) {
            const data = itemsExtractor(model);
            const length = data.length || 0;
            // eslint-disable-next-line import/no-deprecated
            return of({ data, length });
        }
        else {
            const dataQuery = WebMapUtils.getDataQuery(url, id, action);
            const headers = WebMapUtils.getWebApiHeaders();
            this.core.publishEvent(Events.ActionSending);
            return this.http
                .get(dataQuery, { headers, params: parameters })
                .pipe(finalize(() => this.afterHttpRequest()));
        }
    }
    /**
     * Posts the information specified in the data parameter to the server
     * @param url the url for the generic component.
     * @param data the data to be posted
     */
    /* c8 ignore start */
    post(url, data, callback = () => this.afterHttpRequest()) {
        const headers = new HttpHeaders().set(WebMapUtils.WebApiHeader, 'true');
        const id = sessionStorage.getItem(WMConstants.sessionStorageTabIdEntry);
        if (id) {
            headers.set(WMConstants.multiTabHeader, id);
        }
        this.core.publishEvent(Events.ActionSending);
        return this.http.post(url, data, { headers }).pipe(finalize(callback));
    }
    /* c8 ignore stop */
    /**
     * Processes the pending model response from the server. Model changes are
     * expected to be applied during this operation.
     * @param key The identifier of the pending response.
     */
    /* c8 ignore next 5 */
    processPendingResponse(key) {
        this.core.invoke(new PendingResponse(WebMapUtils.PendingResponseHeader, key));
    }
    /**
     * Executes actions after a http invocation.
     */
    /* c8 ignore next 4 */
    afterHttpRequest() {
        this.core.publishEvent('detectChanges', undefined);
        this.core.publishEvent(Events.ActionComplete);
    }
    /**
     * Initialized events batch mode
     */
    startRecordingRequests() {
        this.recordingRequests = true;
        this.recordedRequests = [];
    }
    /**
     * Stops events batch mode
     */
    stopRecordingRequests() {
        this.recordingRequests = false;
        this.recordedRequests = [];
    }
    /**
     * Gets enabled server events
     */
    get enabledServerEvents() {
        return this.sendEventsToServer;
    }
    /**
     * Sets enabled server events
     */
    set enabledServerEvents(value) {
        this.sendEventsToServer = value;
    }
    /**
     * function used to make a server request.
     * @param action Action to execute
     */
    /* c8 ignore next 3 */
    sendAction(action) {
        this.core.sendAction(action);
    }
    /**
     * function used to send a Background Worker action
     * This is because the bkworkers should not wait for responses to finish to be sended
     * @param {IActionModel} action
     * @memberof WebMapService
     */
    /* c8 ignore next 3 */
    sendImmediateWorkerAction(action) {
        this.core.sendImmediateAction(action);
    }
    /**
     * Performs an event call to the server side.
     * @param sender the object that send the action.
     * @param action the action name.
     * @param controller the url to the event controller usually empty.
     * @param serverEvent the name of the event in the server side.
     * @param receiverId the unique id of the current component.
     * @param args the arguments for the current event.
     * @param callback a function to be executed after the send action.
     */
    sendCommand(cmd, callback) {
        if (this.config.webMapVersion === WMConstants.version4) {
            const commandV4 = new Send(cmd.sender, cmd.serverEvent, cmd.args, callback, cmd.highPriority);
            this.handleSendCommandAction(commandV4);
        }
        else {
            const commandV5 = new SendV5(cmd.sender, cmd.action, cmd.receiverId, cmd.serverEvent, cmd.args, callback, cmd.highPriority);
            this.handleSendCommandAction(commandV5);
        }
    }
    /**
     * Send the collected commands to the server and reset the
     * recordedRequest array.
     */
    sendRecordedActions() {
        this.core.sendAction(this.createPerformMultiOperationRequest());
        this.recordedRequests = [];
    }
    /**
     * Handle the commands actions if the recording request flag is on then it is
     * registered in the recordedRequest queue otherwise it is invoked right away.
     * @param command The send command
     */
    handleSendCommandAction(command) {
        if (this.recordingRequests) {
            this.recordedRequests.push(command);
        }
        else {
            this.invoke(command);
        }
    }
    /**
     * Create the request to send all the recorded commands.
     */
    /* c8 ignore start */
    createPerformMultiOperationRequest() {
        if (this.config.webMapVersion === WMConstants.version4) {
            return new ActionModel('ResumeOperation', 'PerformMultiOperation', null, {
                args: this.recordedRequests,
            });
        }
        else {
            return new ActionModelV5('', '', 'ResumeOperation', 'PerformMultiOperation', { args: this.recordedRequests });
        }
    }
    /* c8 ignore stop */
    /**
     * Create the request to send a clean up.
     */
    /* c8 ignore start */
    createPreprareStartUpRequest() {
        if (this.config.webMapVersion === WMConstants.version4) {
            this.core.sendAction(new ActionModel('ResumeOperation', 'prepareStartupApp'));
            window.location.reload();
        }
        else {
            this.post('ResumeOperation/prepareStartupApp', null, () => {
                window.location.reload();
            }).subscribe();
        }
    }
    /* c8 ignore stop */
    /* c8 ignore start */
    shouldCallPendingView(viewId, view) {
        return (!viewId ||
            (view.model.MdiParentInternal &&
                view.model.MdiParentInternal.UniqueID === viewId) ||
            (view.model.ParentInternal &&
                view.model.ParentInternal.UniqueID === viewId) ||
            (view.model.ParentWindow && view.model.ParentWindow.UniqueID === viewId));
    }
    /* c8 ignore stop */
    /**
     * Tries to obtain query params from window.location.href if
     * params parameter is null or empty.
     * @param params Parameter received from init method of app.component.ts
     * migrated application.
     * @returns Returns a queryString params with format [param=1&param=2&para...]
     */
    /* c8 ignore start */
    tryObtainQueryParams(params) {
        if (params && params.length > 0) {
            return params;
        }
        const url = window.location.href;
        if (url.includes('?')) {
            return `${url.split('?')[1]}`;
        }
        return null;
    }
    /* c8 ignore stop */
    /**
     * Process an AppStart action with query params if application runs with
     * WebMap version 4.
     * @param qryParam queryString parameters
     * @returns AppStart action with queryString parameters if it is V4
     */
    /* c8 ignore start */
    processAppStartActionWithQueryParams(qryParam) {
        return ('AppStart' +
            (qryParam && this.config.webMapVersion === WMConstants.version4
                ? `/?${qryParam}`
                : ''));
    }
    /* c8 ignore stop */
    /**
     * Registers browser tab session id
     * @param multipleTabHeader Contains information about if MultiTab features
     * is on and if there is an id assigned for tab session.
     */
    registerBrowserTabSession(multipleTabHeader) {
        if (multipleTabHeader &&
            multipleTabHeader.multipleTabHeader &&
            multipleTabHeader.tabHeaderId) {
            const data = sessionStorage.getItem(WMConstants.sessionStorageTabIdEntry);
            if (!data) {
                sessionStorage.setItem(WMConstants.sessionStorageTabIdEntry, multipleTabHeader.tabHeaderId);
            }
        }
    }
}
/**
 * Current singleton instance of the WebMapService.
 */
WebMapService.currentService = undefined;
WebMapService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapService, deps: [{ token: i1.HttpClient }, { token: WEBMAP_CONFIG }, { token: WEBMAP_APP_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
WebMapService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WEBMAP_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WEBMAP_APP_CONFIG]
                }, {
                    type: Optional
                }] }]; } });

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * @classdesc Intercepts http requests to handle special logic required for
 * WebMAp WebAPI requests.
 */
class WebMapInterceptor {
    /**
     * Creates a new instace of the WebMapHttpInterceptor class.
     * @param webmapService The WebMap Service instance.
     */
    constructor(webmapService) {
        this.webmapService = webmapService;
    }
    /** @inheritdoc */
    intercept(request, next) {
        // Check if we need to intercept the response
        const intercept = WebMapUtils.isWebMapWebAPI(request);
        /* c8 ignore else */
        if (intercept) {
            return next.handle(request).pipe(map(
            /* c8 ignore next 3 */
            (response) => {
                if (response instanceof HttpResponse) {
                    const key = WebMapUtils.getPendingResponseKey(response);
                    /* c8 ignore next 3 */
                    if (key) {
                        this.webmapService.processPendingResponse(key);
                    }
                }
                return response;
            }));
        }
        else {
            return next.handle(request);
        }
    }
}
WebMapInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapInterceptor, deps: [{ token: WebMapService }], target: i0.ɵɵFactoryTarget.Injectable });
WebMapInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: WebMapService }]; } });

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
let InjectorInstance;
/**
 * Contains the list of directives that can be used with WebMap
 */
class WebMapModule {
    /**
     * Provides the WebMapService at a global scope. To be imported only in the root module, for example:
     * @NgModule({
     *   imports: [WebMapModule.forRoot()]
     * })
     */
    /* c8 ignore start */
    static forRoot() {
        return {
            ngModule: WebMapModule,
            providers: [
                { provide: WEBMAP_CONFIG, useValue: WEBMAP_DEFAULT_CONFIG },
                WebMapService,
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: WebMapInterceptor,
                    multi: true,
                },
            ],
        };
    }
    /* c8 ignore stop */
    /* c8 ignore next 3 */
    constructor(injector) {
        this.injector = injector;
        InjectorInstance = this.injector;
    }
}
WebMapModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule });
WebMapModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: WebMapModule, imports: [HttpClientModule, HttpClientXsrfModule] });
WebMapModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapModule, providers: [{ provide: WEBMAP_CONFIG, useValue: WEBMAP_DEFAULT_CONFIG }], imports: [HttpClientModule, HttpClientXsrfModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WebMapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule, HttpClientXsrfModule],
                    providers: [{ provide: WEBMAP_CONFIG, useValue: WEBMAP_DEFAULT_CONFIG }],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class HttpServer {
    constructor(inject, httpClient) {
        this.inject = inject;
        this.pendingResp = 0;
        this.parseResponse = function (response) {
            let result = {};
            result = response.body || {};
            result['status'] = response.status;
            result['responseText'] = JSON.stringify(response.body);
            result['headers'] = response.headers;
            return result;
        };
        this.inject = inject || /* c8 ignore next */ Inject$1.Instance;
        this.event = this.inject.resolve(Constants.EventAggregator);
        this.http =
            httpClient /* c8 ignore next */ ||
                InjectorInstance.get(HttpClient);
    }
    /**
     * Returns the amount of pending responses in order
     * to verify if we can remove the busy loader in the front end.
     */
    get pendingResponses() {
        return this.pendingResp;
    }
    get(url, params, callback) {
        const httpParams = params;
        this.pendingResp++;
        this.http
            .get(url, {
            observe: 'response',
            params: { httpParams },
            responseType: 'json',
        })
            .subscribe({
            next: (response) => {
                this.pendingResp--;
                this.executeCallback(response, callback);
            },
            error: (errorResponse) => {
                this.pendingResp--;
                this.errorResponse(errorResponse, callback);
            },
        });
    }
    post(url, data, callback, config, eventName) {
        this.sendRequest(url, data, config, eventName)
            .subscribe({
            next: (result) => {
                this.executeCallback(result, callback);
                this.pendingResp--;
            },
            error: (errorResponse) => {
                this.errorResponse(errorResponse, callback);
            },
        })
            .add(() => {
            this.event.publish(Events.ActionComplete, url);
        });
    }
    errorResponse(errorResponse, callback) {
        this.pendingResp--;
        this.NotifyError(errorResponse);
        this.executeCallback(errorResponse, callback);
    }
    NotifyError(response) {
        const errorResponse = this.parseResponse(response);
        if (errorResponse.status) {
            this.event.publish(Events.Error, new NetworkException(errorResponse.responseText, errorResponse.status));
        }
    }
    sendRequest(url, data, config, eventName) {
        config = config || new RequestConfig();
        config.contentType = config.contentType || ContentType.applicationJson;
        this.pendingResp++;
        if (config.contentType === ContentType.multipartFormdata) {
            return this.processFormData(url, data);
        }
        else {
            return this.processDefault(url, data, config, eventName);
        }
    }
    processFormData(url, data) {
        this.event.publish(Events.ActionSending, url);
        return this.http.post(url, data, {
            headers: { WM: 'true' },
            observe: 'response',
        });
    }
    processDefault(url, data, config, eventName) {
        // add WebMap headers
        config.headers.WM = 'true';
        // add Multi Tab header if it exists
        const id = sessionStorage.getItem(WMConstants.sessionStorageTabIdEntry);
        if (id) {
            config.headers[WMConstants.multiTabHeader] = id;
        }
        let headers = new HttpHeaders(config.headers);
        headers = headers.set('Content-Type', config.contentType);
        this.event.publish(Events.ActionSending, url, eventName);
        return this.http.post(url, data, {
            headers,
            observe: 'response',
        });
    }
    executeCallback(response, callback) {
        if (callback) {
            callback(this.parseResponse(response));
        }
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * This is a collection used to save the strings of the BackGroundWorkers
 * related events
 */
const bgWorkerEvents = [
    'BackgroundWorkerStarted',
    'BackgroundWorkerDoWork',
    'BackgroundWorkerCompleted',
];
class LoadingModal extends Modal {
    constructor() {
        super();
    }
    name() {
        return 'loading';
    }
    show(args) {
        /* c8 ignore start */
        if ((args.eventName && bgWorkerEvents.includes(args.eventName)) ||
            (window['app'] &&
                window['app'].disableLoading &&
                window['app'].disableLoading === true)) {
            args.show = false;
        }
        /* c8 ignore stop */
        triggerEvent(args);
    }
}
/**
 * Trigger an event that tells the loading component
 * to show or to hide according the param value.
 * @param show The value that indicates if the
 * loading component is visible.
 */ function triggerEvent(args) {
    /* c8 ignore next 5 */
    if (WebMapService.currentService) {
        WebMapService.currentService.core
            .getEvent()
            .publish('DisplayLoading', args);
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class SessionExpiredModal extends Modal {
    constructor() {
        super();
    }
    name() {
        return 'expired';
    }
    /** Trigger an event to navigate
     * to the session expired component
     * This event is handled by the formscontainer component
     */
    /* c8 ignore start */
    show(args) {
        WebMapService.currentService.core
            .getEvent()
            .publish('navigateSystemDialog', {
            dialog: 'SessionExpired',
            model: { arguments: args.arguments[0] },
        });
    }
}

let UrlResolver = class UrlResolver {
    resolveUrl(action) {
        return this.formatUrl(action);
    }
    formatUrl(actionModel) {
        const controller = actionModel.controller;
        const action = actionModel.action;
        const queryString = actionModel.queryString;
        const target = getTargetId(actionModel);
        let url = WebMapService.currentService.config.baseHref;
        if (controller && controller.length > 0) {
            url += controller;
        }
        if (action && action.length > 0) {
            /* c8 ignore next 3  */
            if (url.length > 0) {
                url += '/';
            }
            url += action;
        }
        if (target && target.length > 0) {
            /* c8 ignore next 3  */
            if (url.length > 0) {
                url += '/';
            }
            url += target;
        }
        if (queryString && queryString.length > 0) {
            if (url.length > 0) {
                url += '/';
            }
            url += `?${queryString}`;
        }
        if (url.length === 0) {
            url = '/';
        }
        return url;
    }
};
UrlResolver = __decorate([
    ExceptionHandlerClass(ErrorCodes.AngularClient)
], UrlResolver);
function getTargetId(action) {
    if (action.receiverId) {
        // For WM5
        return action.receiverId;
    }
    return encodeURIComponent(action.item ? action.item.UniqueID : ''); // For WM4
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class ChangeDetectionWorker {
    constructor(inject = null) {
        this.inject = inject;
        this.inject = inject || Inject$1.Instance;
        this.event = this.inject.resolve(Constants.EventAggregator);
    }
    process(response) {
        response?.changed.forEach((elem) => {
            this.event.publish('detectChanges', elem.Id);
        });
    }
    get Order() {
        return Order.POST;
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class SyncCompletedWorker {
    constructor(inject = null) {
        this.inject = inject;
        this.inject = inject || Inject$1.Instance;
        this.event = this.inject.resolve(Constants.EventAggregator);
    }
    process(response) {
        // This setTimeout is required, because the controls may not be created at this time.
        // Please note that created does not mean it has already been rendered.
        setTimeout(() => {
            this.event.publish('SyncCompleted', undefined);
        }, 100);
    }
    get Order() {
        return Order.POST;
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class ModalsName {
}
ModalsName.messageBox = 'MSGBOX';
ModalsName.fileExplorer = 'FileExplorer';

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class MessageBox extends Modal {
    name() {
        return ModalsName.messageBox;
    }
    /**
     * Trigger an event that tells the MessageBox component
     * to show or to hide according the param value.
     * @param params The value that indicates if the
     * loading component is visible.
     */
    /* c8 ignore next 7 */
    show(params) {
        if (WebMapService.currentService) {
            WebMapService.currentService.core
                .getEvent()
                .publish('MessageBox', params);
        }
    }
}

let RequestBuilder = class RequestBuilder {
    constructor(inject = null) {
        this.inject = inject;
        this.inject = inject || Inject$1.Instance;
        this.library = this.inject.resolve(Constants.Library);
    }
    create(action) {
        let request;
        switch (action.requestConfig.requestType) {
            case RequestType.ModelRequest:
                request = new Request();
                request.vm = action.item ? action.item.UniqueID : '';
                request.arguments = action.parameters;
                break;
            case RequestType.RawRequest:
                request = action.parameters;
                break;
            default:
                request = new Request();
                request.parameters = action.parameters;
                break;
        }
        return request;
    }
};
RequestBuilder = __decorate([
    ExceptionHandlerClass(ErrorCodes.AngularClient),
    __metadata("design:paramtypes", [Object])
], RequestBuilder);

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class FileExplorer extends Modal {
    name() {
        return ModalsName.fileExplorer;
    }
    /**
     * Trigger an event that tells the FileExplorer component
     * to show or to hide according the param value.
     * @param params The value that indicates if the
     * loading component is visible.
     */
    /* c8 ignore start */
    show(params) {
        if (WebMapService.currentService) {
            WebMapService.currentService.core
                .getEvent()
                .publish('FileExplorer', params);
        }
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class AlertModal extends Modal {
    constructor() {
        super();
    }
    name() {
        return 'alert';
    }
    /** Trigger an event to navigate
     * to the alert component
     * This event is handled by the formscontainer component
     */
    /* c8 ignore next 5 */
    show(args) {
        WebMapService.currentService.core
            .getEvent()
            .publish('alert', { model: args });
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class AngularView extends View {
    /* c8 ignore start */
    constructor(model, inject) {
        super(model, inject);
        this.id = model.UniqueID;
    }
    /* c8 ignore next */
    bindings() { }
    /* c8 ignore next 4 */
    render() {
        const eventHandler = Inject$1.Instance.resolve(Constants.EventAggregator);
        eventHandler.publish('navigateToView', this.id);
    }
    /* c8 ignore next 4 */
    dispose() {
        const eventHandler = Inject$1.Instance.resolve(Constants.EventAggregator);
        eventHandler.publish('disposeView', this.id);
    }
    /* c8 ignore next 4 */
    close() {
        const eventHandler = Inject$1.Instance.resolve(Constants.EventAggregator);
        eventHandler.publish('disposeView', this.id);
    }
    /* c8 ignore next 4 */
    focus() {
        const eventHandler = Inject$1.Instance.resolve(Constants.EventAggregator);
        eventHandler.publish('setTopView', this.id);
    }
    /* c8 ignore next */
    show() { }
    /* c8 ignore next */
    hide() { }
    /* c8 ignore next 4 */
    bringToFront() {
        const eventHandler = Inject$1.Instance.resolve(Constants.EventAggregator);
        eventHandler.publish('bringToFront', this.route);
    }
    /* c8 ignore next 3 */
    isPendingLoad(view, views) {
        return false;
    }
}

class AngularViewWF extends AngularView {
    /* c8 ignore start */
    constructor(model, inject) {
        super(model, inject);
    }
    /* c8 ignore stop */
    /**
     * Verify if the parent container is actually loaded or pending to load
     * @param {IView} view
     * @param {Array<IView>} views
     *
     * @return True if the parent container is pending to load, otherwise return False
     */
    isPendingLoad(view, views) {
        if (view.model.MdiParentInternal) {
            return !view.model.MdiParentInternal.IsPendingViewLoaded;
        }
        else if (view.model.ParentInternal) {
            return views.length > 1;
        }
        else {
            return false;
        }
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class ViewResolver {
    // eslint-disable-next-line
    /* c8 ignore next 3 */
    resolve(model, load) {
        load(new AngularView(model));
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class ViewResolverWF extends ViewResolver {
    // eslint-disable-next-line
    /* c8 ignore next 3 */
    resolve(model, load) {
        load(new AngularViewWF(model));
    }
}

class AngularViewPB extends AngularView {
    /* c8 ignore start */
    constructor(model, inject) {
        super(model, inject);
        /* c8 ignore stop */
        /* c8 ignore start */
        this.hasMDI = (views) => {
            for (const view of views) {
                if (view.model.WindowType === 3 ||
                    view.model.WindowType === 2) {
                    return true;
                }
            }
            return false;
        };
    }
    /* c8 ignore stop */
    /* c8 ignore start*/
    isPendingLoad(view, views) {
        return (views.length > 1 && view.model.ParentWindow && this.hasMDI(views));
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
class ViewResolverPB extends ViewResolver {
    // eslint-disable-next-line
    /* c8 ignore next 3 */
    resolve(model, load) {
        load(new AngularViewPB(model));
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * @classdesc Implements the Angular version of the WebMap Core Application.
 */
class AngularApp {
    /**
     * Creates a new instance of the App class that contains angular-specific
     * behavior.
     */
    constructor(serviceConfig, config) {
        this.appVersion = serviceConfig.webMapVersion;
        this.webMapConfig = serviceConfig;
        this.appConfig = config;
        this.currentApp =
            this.appVersion === WMConstants.version4 ? new App() : new AppV5();
        this.setupApp(serviceConfig.webMapVersion);
    }
    setupApp(version) {
        if (this.appConfig) {
            this.currentApp.extCommandRegister = this.appConfig.onCommandRegister;
            this.currentApp.extCommandHandlerRegister =
                this.appConfig.onCommandHandlerRegister;
            this.currentApp.extOnSystemEvent = this.appConfig.onSystemEvent;
        }
        this.currentApp.onRegister =
            this.appVersion === WMConstants.version5
                ? this.angularRegisterV5
                : this.angularRegister;
        this.currentApp.onViewHandler = this.viewHandler;
        this.currentApp.onDeltaHandler = this.deltaHandlerInit;
        this.currentApp.getModel = this.getModel;
        this.currentApp.addModel = this.addModel;
        this.currentApp.getEvent = this.getEvent;
        this.currentApp.invoke = this.invoke;
        this.currentApp.angularRegister = this.angularRegister;
        this.currentApp.onCommandRegister = this.angularOnCommandRegister;
        this.currentApp.onResolver =
            this.appVersion === WMConstants.version5
                ? undefined
                : this.angularOnResolver;
        this.currentApp.webMapConfig = this.webMapConfig;
        this.currentApp.styleProperties = this.webMapConfig.styleProperties;
    }
    /**
     * Returns event object from this App.
     */
    getEvent() {
        return this.event;
    }
    /**
     * Invokes a Command.
     * @param command The command to invoke.
     */
    invoke(command) {
        this.invoker.invoke(command);
    }
    /**
     * Returns the model with the specified Unique Id.
     * @param uniqueId The identifier of the model.
     */
    getModel(uniqueId) {
        return this.models.getModel(uniqueId);
    }
    /**
     * Adds a new modelV5 in models
     * @param newModel The model.
     */
    addModel(model) {
        this.models.add(new ModelV5(model.Id, model));
    }
    /**
     * Applies angular-specific configuration to the dependency injector.
     * @param inject The dependency injector.
     */
    angularRegister(inject) {
        inject.register(Constants.UrlResolver, UrlResolver, Resolve.Singleton);
        inject.register(Constants.Server, HttpServer, Resolve.Singleton);
        inject.register(Constants.ViewResolver, this.webMapConfig.wmSourceVersion === WMConstants.sourceVersionPB
            ? ViewResolverPB
            : ViewResolverWF, Resolve.Singleton);
        inject.register(Constants.RequestBuilder, RequestBuilder, Resolve.Singleton);
    }
    /**
     * Applies angular-specific configuration to the dependency injector.
     * @param inject The dependency injector.
     */
    angularRegisterV5(inject) {
        this.angularRegister(inject);
        inject.register(Constants.RequestBuilder, RequestBuilderV5, Resolve.Singleton);
    }
    /**
     * Applies deltaHandler workers registration
     */
    deltaHandlerInit() {
        this.deltaHandler.registerWorker(new ChangeDetectionWorker());
        this.deltaHandler.registerWorker(new SyncCompletedWorker());
    }
    /**
     * Register new Modal Components
     * @param viewManager the webmap's viewmanager object
     */
    viewHandler(viewManager) {
        viewManager.registerModal(new LoadingModal());
        viewManager.registerModal(new SessionExpiredModal());
        viewManager.registerModal(new MessageBox());
        viewManager.registerModal(new FileExplorer());
        viewManager.registerModal(new AlertModal());
        /* c8 ignore next 3 */
        if (this.extCommandHandlerRegister) {
            this.extCommandHandlerRegister(viewManager);
        }
    }
    /**
     * This method allows to override existing command definitions or
     * to register new ones
     */
    /* c8 ignore start */
    angularOnCommandRegister(factory) {
        if (this.extCommandRegister) {
            this.extCommandRegister(factory);
        }
        if (this.extOnSystemEvent) {
            const eventAggregator = this.currentApp.inject.resolve(Constants.EventAggregator);
            this.extOnSystemEvent(eventAggregator);
        }
    }
    /* c8 ignore stop */
    /* c8 ignore next 3 */
    angularOnResolver(modelResolver) {
        modelResolver.registerBehavior(new EventBehavior(), true);
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Used to define the parameters of a Send Command call used by both
 * WebMap5 and WebMap 4 protocols.
 */
class AngularCommand {
    /* c8 ignore start */
    constructor(sender, action, controller = '', serverEvent, receiverId, args, highPriority) {
        this.sender = sender;
        this.action = action;
        this.controller = controller;
        this.serverEvent = serverEvent;
        this.receiverId = receiverId;
        this.args = args;
        this.highPriority = highPriority;
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
var CommandType;
(function (CommandType) {
    CommandType["Create"] = "CreateData";
    CommandType["Delete"] = "DeleteData";
    CommandType["Update"] = "UpdateData";
})(CommandType || (CommandType = {}));

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/*
The DataCommand class is used to manage command
object related with data
 */
class DataCommand {
    /**
     * This function is used to bind the command and
     * register to bind command in WebMapService
     *
     * @memberof DataCommand
     */
    register() {
        this.bindCommand = this.create.bind(this);
        WebMapService.currentService.core
            .getEvent()
            .publish(Events.PreActionCommandGeneratorRegistration, this.bindCommand);
    }
    /**
     * If the command has changes, create a new command object
     * with the receiverId, command name, and
     * data. Then clean the command object.
     * @returns The result is an object DataCommand
     *
     * @memberof DataCommand
     */
    create() {
        const tmpData = {};
        if (this.name == CommandType.Update && this.data) {
            return this.buildUpdateContent();
        }
        else if (this.data && this.data.length > 0) {
            return this.buildContent();
        }
        return null;
    }
    /**
     * Clean the data of the command and set false
     * hasChanges flag
     *
     * @memberof DataCommand
     */
    clean() {
        this.hasChanges = false;
        this.data = undefined;
    }
    /**
     * Update command properties
     * @param data : object with the properties custom related
     * with data
     * @param properties : object with the properties related
     * with styles and functionality
     * @param commandType - The type of the command.
     *
     * @memberof DataCommand
     */
    update(data) {
        if (this.name == CommandType.Update) {
            this.data = data;
        }
        else {
            this.data.push(data);
        }
        this.hasChanges = true;
    }
    /**
     * This function is called  when we needs to execute an
     * un-registration event in the command
     *
     * @memberof DataCommand
     */
    destroy() {
        WebMapService.currentService.core
            .getEvent()
            .publish(Events.PreActionCommandGeneratorDeregistration, this.bindCommand);
    }
    buildContent() {
        const tmpData = {};
        tmpData['Data'] = this.data.map((data) => {
            const target = data.target;
            this.cleanDelta(target);
            return data.data(target);
        });
        if (this.hasChanges) {
            const result = {
                receiverId: this.receiverId,
                command: this.name,
                data: tmpData,
            };
            this.clean();
            return result;
        }
        return null;
    }
    buildUpdateContent() {
        const current = this.data.data(this.data.target);
        this.cleanDelta(this.data.target);
        if (this.hasChanges) {
            const result = {
                receiverId: this.receiverId,
                command: this.name,
                data: current,
            };
            this.clean();
            return result;
        }
        return null;
    }
    cleanDelta(target) {
        if (target.__delta) {
            target.__delta = {
                changes: [],
                dataChanges: [],
                isNew: false,
                isDirty: true,
                propsTargets: {},
            };
        }
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/*
This service manages the commands Array that stores the commands
related with Data(DataCommand)
*/
class DataCommandService {
    /**
     * This function returns a DataCommand object from the commands Map object
     * @param id - The id of the command to get.
     * @returns A DataCommand object.
     *
     * @memberof DataCommandService
     */
    getCommand(id, type) {
        return DataCommandService.commands.find((command) => command.receiverId === id && command.name === type);
    }
    /**
     * If a command exists, update it, otherwise register it.
     * @param receiverId - The id of the element that will receive the command.
     * @param commandType - CommandType - this is an enum that has the following values:
     * @param data : object with the properties custom related
     * with data
     * @param properties : object with the properties related
     * with styles and functionality
     *
     * @memberof DataCommandService
     */
    addCommand(receiverId, commandType, object) {
        const command = this.getCommand(receiverId, commandType);
        const data = object;
        if (command) {
            this.updateDataCommand(command, data);
        }
        else {
            this.registerDataCommand(receiverId, data, commandType);
        }
    }
    /**
     * This function builds a command and registers it, and also
     *  sets it in the commands map.
     * @param {string} receiverId - The id of the element that will receive the data.
     * @param data : object with the properties custom related
     * with data
     * @param properties : object with the properties related
     * with styles and functionality
     * @param {CommandType} commandType - CommandType
     *
     * @memberof DataCommandService
     */
    registerDataCommand(receiverId, data, commandType) {
        const command = this.buildDataCommand(receiverId, data, commandType);
        command.register();
        DataCommandService.commands.push(command);
    }
    /**
     * Create an new object DataCommand.
     * @param receiverId - The id of the receiver that will receive the command.
     * @param data : object with the properties custom related
     * with data
     * @param properties : object with the properties related
     * with styles and functionality
     * @param commandType - CommandType
     * @returns A DataCommand object.
     *
     * @memberof DataCommandService
     */
    buildDataCommand(receiverId, data, commandType) {
        const command = new DataCommand();
        command.receiverId = receiverId;
        if (commandType === CommandType.Update) {
            command.data = data;
        }
        else {
            command.data = [data];
        }
        command.hasChanges = true;
        command.name = commandType;
        return command;
    }
    /**
     * This function updates the command data.
     * @param commandType - CommandType - The type of command to update.
     * @param  command - The command object that is being updated.
     * @param data : object with the properties custom related
     * with data
     * @param properties : object with the properties related
     * with styles and functionality
     *
     * @memberof DataCommandService
     */
    updateDataCommand(command, data) {
        command.update(data);
    }
    /**
     * For each key in the commands object, call the destroy function on the command
     * object and then clear the commands object, but just if the command was executed.
     *
     * @memberof DataCommandService
     */
    ngOnDestroy() {
        DataCommandService.commands = DataCommandService.commands.filter((command) => {
            if (!command.hasChanges) {
                command.destroy();
                return false;
            }
            return true;
        });
    }
}
// Register commands related with the manage of data
DataCommandService.commands = [];
DataCommandService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DataCommandService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DataCommandService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DataCommandService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DataCommandService, decorators: [{
            type: Injectable
        }] });

class SocketService {
    constructor() {
        /**
         * Store the webSocket that is being used.
        @memberof SocketService
        */
        this.webSocket = undefined;
    }
    /**
     * Returns a string that s the URL of the WebSocket.
     * @returns
     * @memberof SocketService
     */
    getURL() {
        const origin = window.location.origin;
        const protocol = window.location.protocol;
        const urlBase = protocol === 'http:' ? 'ws:' : 'wss:';
        return urlBase.concat(origin.slice(protocol.length, origin.length), '/dws');
    }
    /**
     * Sets up a websocket connection to the server and binds the
     * functions to the websocket events.
     * @memberof SocketService
     */
    setupSocketConnection() {
        this.webSocket = new WebSocket(this.getURL());
        this.webSocket.onopen = this.onSocketOpen.bind(this);
        this.webSocket.onclose = this.onSocketClose.bind(this);
        this.webSocket.onmessage = this.onSocketMessage.bind(this);
    }
    /**
     * Call this function when the state of the sockets is OPEN
     * @memberof SocketService
     */
    onSocketOpen() { }
    /**
     * If the state of the connection is CLOSE, handle the error
     * @param {CloseEvent} event - CloseEvent - The event that was triggered when the
     * socket was closed.
     * @memberof SocketService
     */
    onSocketClose(event) {
        if (!event.wasClean) {
            this.handleError(event);
        }
    }
    /**
     * When the socket receives a message, call the handleMessage function and pass
     * it the event.
     * @param event - The event object that was passed to the function.
     */
    onSocketMessage(event) {
        this.handleMessage(event);
    }
    /**
     * Throws a new NetworkException with the message 'Sockets error' and the code
     * @memberof SocketService
     */
    /* c8 ignore next 3 */
    handleError(event) {
        throw new NetworkException('Sockets error', event?.code ?? 1006);
    }
    /**
     * Sends a message to the WebSocket
     * @param {IMessageCollections} message - IMessageCollections
     * @memberof SocketService
     */
    sendMessage(message) {
        this.webSocket.send(JSON.stringify(message));
    }
    /**
     * Receives a message from the WebSocket, and apply in the models that get from
     * WebMapService
     * @param event
     * @memberof SocketService
     */
    handleMessage(event) {
        const message = JSON.parse(event.data);
        const collectionModel = WebMapService.currentService.core.getModel(message.UniqueId);
        if (collectionModel) {
            Object.getPrototypeOf(collectionModel)._lambdasReceiver[message.Action.toLowerCase()]?.apply(collectionModel, message.Arguments);
        }
    }
}
SocketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SocketService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SocketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SocketService });
__decorate([
    ExceptionHandler()
    /* c8 ignore next 3 */
    ,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [CloseEvent]),
    __metadata("design:returntype", void 0)
], SocketService.prototype, "handleError", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SocketService, decorators: [{
            type: Injectable
        }], propDecorators: { 
        /* c8 ignore next 3 */
        handleError: [] } });

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/* Wrapper class that takes a service type and then sets the service to the
appropriate service. */
class MessagingService {
    /**
     * Creates a new instance of MessagingService
     * @param {SocketService} socketService - SocketService -
     */
    /* c8 ignore start */
    constructor(socketService) {
        this.socketService = socketService;
    }
    /* c8 ignore stop */
    /**
     * If the serviceType is 'socket', then set the service to the socketService,
     * and then call the init() function on the socketService.
     * @param {'socket' | 'http'} serviceType - 'socket' | 'http'
     * @memberof MessagingService
     */
    /* c8 ignore next */
    init(serviceType = 'http') {
        MessagingService.currentService = this;
        if (serviceType === 'socket') {
            this.service = this.socketService;
            this.service.setupSocketConnection();
        }
    }
    /**
     * This function takes a message object and passes it to the sendMessage function
     * in the service.
     * @param {IMessageCollections} message - IMessageCollections
     * @memberof MessagingService
     */
    sendMessage(message) {
        if (this.service) {
            this.service.sendMessage(message);
        }
    }
}
/**
 * Store the current service.
 * @memberof MessagingService
 * */
MessagingService.currentService = undefined;
MessagingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MessagingService, deps: [{ token: SocketService }], target: i0.ɵɵFactoryTarget.Injectable });
MessagingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MessagingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MessagingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SocketService }]; } });

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/* c8 ignore start */
/* c8 ignore stop */

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Decorates a Property Setter to notify the WebMap Service about the change.
 */
function NotifyChange(property, projectVersion) {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.set;
        descriptor.set = function (...args) {
            // Execute original setter
            // eslint-disable-next-line no-invalid-this
            const result = originalMethod.apply(this, args);
            // this if check if the model is new,
            // if the model is new return and don't use delta option
            //to send the changes
            if (this.model?.__delta?.isNew === true) {
                return;
            }
            // Avoid executing the method if should be ignored by protocol
            if (projectVersion &&
                projectVersion !== WebMapService.currentService.config.webMapVersion) {
                return;
            }
            // Execute change tracking
            if (WebMapService.currentService) {
                // eslint-disable-next-line no-invalid-this
                if (this.model && property) {
                    WebMapService.currentService.notifyChange(this.model, property);
                }
                else if (!this[ModelFactoryV5.isInitializingModel]) {
                    // if is a nested custom model, send this
                    WebMapService.currentService.notifyChange(this, key);
                }
            }
            else {
                WMLogger.instance().error('Trying to notify a property change but the WebMap Service is not available');
            }
            return result;
        };
        return descriptor;
    };
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Decorates a Property to notify the WebMap Service about the change.
 *  @param property ? : string
 *  @param projectVersion ? : string
 */
function NotifyPropertyChange(property, projectVersion) {
    return (target, key, descriptor) => {
        if (property) {
            key = property;
        }
        return Object.assign({}, descriptor, {
            get: function () {
                return this.model ? this.model[key] : '';
            },
            set: function (newValue) {
                if (!this.model) {
                    return;
                }
                // Avoid executing the method if should be ignored by protocol
                if (projectVersion &&
                    WebMapService.currentService &&
                    projectVersion !== WebMapService.currentService.config.webMapVersion) {
                    return;
                }
                if (WebMapService.currentService) {
                    // eslint-disable-next-line no-invalid-this
                    this.model[key] = newValue;
                    WebMapService.currentService.notifyChange(this.model, key);
                }
                else {
                    WMLogger.instance().error('Trying to notify a property change but the WebMap Service is not available');
                }
            },
        });
    };
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Data resolver is created to manage the references when we use add decorator
 * the idea is manage the model in target and the data to command in 'data'
 * @export
 * @class DataResolver
 */
class DataResolver {
    /**
     * Creates an instance of DataResolver.
     * this.data is a lambda to resolve in the last moment to send the BE
     * this.target save the model to keep de reference
     * @param {*} args
     * @param {*} functionAux
     * @memberof DataResolver
     */
    constructor(args, functionAux) {
        this.data = (target) => functionAux(target);
        this.target = args;
    }
}

/** *********************************************************************

Copyright (C) Mobilize.Net  info@mobilize.net - All Rights Reserved
This file is part of the Mobilize Frameworks, which is
proprietary and confidential.
NOTICE: All information contained herein is, and remains
the property of Mobilize.Net  Corporation.
The intellectual and technical concepts contained herein are
proprietary to Mobilize.Net  Corporation and may be covered
by U.S. Patents, and are protected by trade secret or copyright law.
Dissemination of this information or reproduction of this material
is strictly forbidden unless prior written permission is obtained
from Mobilize.Net  Corporation.
This file is subject to the terms and conditions defined in
file 'LICENSE.txt', which is part of this source code package.
***********************************************************************

/** @type {*}
 * list a propierties that we need to delete
 * to have the model cleaner
*/
const listOfPropertiesToDelete = [
    'childrenToNotify',
    'eventChanges',
    'events',
    'id',
    'mapperId',
    'myInternalProperties',
    'myIsModalView',
    'myIsTempModel',
    'myPointers',
    'myUniqueID',
    'referencedBy',
    'references',
    'stylePivot',
    'IsModalView',
    'IsTempModel',
    'UniqueID',
    'isArray',
    'isPointer',
    '__delta',
    'Id',
    'data',
];

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Decorator of function to send command of type CreateData
 */
function Add() {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            const result = originalMethod.apply(this, args);
            const object = new DataResolver(args[0], createAuxObject);
            this.dataCommandService.addCommand(this.id, CommandType.Create, object);
            return result;
        };
        return descriptor;
    };
}
/**
 * Here we create a auxiliar Object with the next structure
 *  { MobilizedId: "1234",
 *    data : { custom properties},
 *    props : { MapperId and styles or properties related with the behavior}
 *  }
 * we must delete many properties because the model return in some cases may
 * properties and we just need a same structure without more properties that we need
 *
 * @param {*} args
 * @return {*}
 */
const createAuxObject = (args) => {
    const listToRemove = listOfPropertiesToDelete;
    const argsCopy = { ...args };
    const { Id, data } = argsCopy;
    listToRemove.forEach((arg) => {
        delete argsCopy[`${arg}`];
    });
    const auxObj = {
        MobilizeId: Id,
        data: data ?? {},
        properties: {
            ...argsCopy,
        },
    };
    return { ...auxObj };
};

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Decorator of function to send command of type DeleteData
 */
function Delete() {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            const result = originalMethod.apply(this, args);
            const object = new DataResolver(args[0], createAuxObjectDelete);
            this.dataCommandService.addCommand(this.id, CommandType.Delete, object);
            return result;
        };
        return descriptor;
    };
}
/**
 * Here we create a auxiliar Object that return the MobilizeId
 * @param {string} id
 * @return {*}
 */
const createAuxObjectDelete = (id) => {
    const auxObj = { MobilizeId: id };
    return { ...auxObj };
};

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Decorator of function to send command of type UpdateData, this decorator
 * should be used when we needs to send changes in properties (style, enable...)
 * @param {string} property  - The name of the property that is being updated.
 */
function Update(property) {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.set;
        descriptor.set = function (...args) {
            const result = originalMethod.apply(this, args);
            verifyDelta(this.model);
            if (!this.model.__delta.isNew) {
                registerDelta(this.model, this.id, this.dataCommandService);
                registerPropertyChange(this.model, property);
            }
            return result;
        };
        return descriptor;
    };
}
/**
 * Decorator of function to send command of type Update with target, this decorator
 * should be used when we needs to send changes in properties (style, enable...)
 * and the binf should enable to resolve the value from a bound function.
 * @param {string} property  - The name of the property that is being updated.
 * @param property  - The name of the property that is being updated.
 * @param bind - The target function to resolve the value
 */
function UpdateWithTarget(property, targetFunc) {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.set;
        descriptor.set = function (...args) {
            const result = originalMethod.apply(this, args);
            verifyDelta(this.model);
            if (!this.model.__delta.isNew) {
                registerDelta(this.model, this.id, this.dataCommandService);
                registerPropertyChange(this.model, property);
                if (this.model.__delta.propsTargets[property] == undefined) {
                    this.model.__delta.propsTargets[property] = targetFunc.bind(this);
                }
            }
            return result;
        };
        return descriptor;
    };
}
/**
 * Decorator of function to send command of type UpdateData, this decorator
 * should be used when we needs to send changes in data
 */
function UpdateData(dataFunc) {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            const result = originalMethod.apply(this, args);
            verifyDelta(this.model);
            if (!this.model.__delta.isNew) {
                registerDelta(this.model, this.id, this.dataCommandService);
                registerDataChange(this.model, args[0]);
                if (this.model.__delta.propsTargets[targetDataName] == undefined) {
                    this.model.__delta.propsTargets[targetDataName] = dataFunc.bind(this);
                }
            }
            return result;
        };
        return descriptor;
    };
}
const targetDataName = '___@@@DataName';
/**
 * Resolves updates registered on a model.
 * @param model The model where the changes are located.
 * @returns Json object with all changes required to be updated on the command.
 */
const resolveUpdates = (model) => {
    const result = { Properties: {}, Data: {}, MobilizeId: model.Id };
    model.__delta.changes.forEach((element) => {
        if (model.__delta.propsTargets[element]) {
            result.Properties[element] = model.__delta.propsTargets[element]();
        }
        else {
            result.Properties[element] = model[element];
        }
    });
    if (model.__delta.propsTargets[targetDataName]) {
        const targetDataFunc = model.__delta.propsTargets[targetDataName];
        model.__delta.dataChanges.forEach((element) => {
            result.Data[element] = targetDataFunc(element);
        });
    }
    return result;
};
function registerDelta(model, id, dataCommandService) {
    if (!model.__delta.isDirty) {
        const object = new DataResolver(model, resolveUpdates);
        dataCommandService.addCommand(id, CommandType.Update, object);
        model.__delta.isDirty = true;
    }
}
function registerPropertyChange(model, property) {
    if (!model.__delta.changes.includes(property)) {
        model.__delta.changes.push(property);
    }
}
function registerDataChange(model, name) {
    if (!model.__delta.dataChanges.includes(name)) {
        model.__delta.dataChanges.push(name);
    }
}
function verifyDelta(model) {
    if (model.__delta == undefined) {
        model.__delta = {
            changes: [],
            dataChanges: [],
            isNew: false,
            isDirty: false,
            propsTargets: {},
        };
    }
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/
/**
 * Decorates a methods in the collections, to sendMessages with the updates in
 * the collections and create a reference of the method to call later
 * @memberof UpdateCollection
 */
function SendMessage(action) {
    return function (target, key, descriptor) {
        const originalMethod = descriptor.value;
        const lambda = action ?? key;
        // Stores a lambda in the prototype of the collection
        target['_lambdasReceiver'] = {
            [lambda.toLowerCase()]: descriptor.value,
            ...target._lambdasReceiver,
        };
        // Call sendMessage in the MessagingService
        descriptor.value = function (...args) {
            const result = originalMethod.apply(this, args);
            MessagingService?.currentService?.sendMessage({
                Arguments: args,
                UniqueId: this.id,
                Action: lambda.toLocaleLowerCase(),
            });
            return result;
        };
        return descriptor;
    };
}

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/

/** *********************************************************************
 * Copyright (C) Mobilize.Net <info@mobilize.net> - All Rights Reserved
 *
 * This file is part of the Mobilize Frameworks, which is
 * proprietary and confidential.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobilize.Net Corporation.
 * The intellectual and technical concepts contained herein are
 * proprietary to Mobilize.Net Corporation and may be covered
 * by U.S. Patents, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobilize.Net Corporation.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 ***********************************************************************/

/**
 * Generated bundle index. Do not edit.
 */

export { Add, AngularApp, AngularCommand, ChangeDetectionWorker, DataCommandService, Delete, InjectorInstance, LoadingModal, MessagingService, NotifyChange, NotifyPropertyChange, SendMessage, SessionExpiredModal, SocketService, SyncCompletedWorker, Update, ViewResolver, WEBMAP_APP_CONFIG, WEBMAP_CONFIG, WEBMAP_DEFAULT_CONFIG, WMConstants, WebMapInterceptor, WebMapModule, WebMapService, WebMapUtils };
